import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import styled, { css } from "styled-components";
import { IconCheck } from "@bynder/icons";
import { token } from "./token.js";
import { Frame } from "./Frame.js";
import { CardContext } from "./context.js";
import { Header } from "./Header.js";
import { AdditionalInfo } from "./AdditionalInfo.js";
import { ContextActionButton } from "./ContextActionButton.js";
import { Images, Img } from "./Images.js";
import { FilePlaceholder } from "./FilePlaceholder.js";
import { useId } from "./useId.js";
import { IconPlaceholder } from "./IconPlaceholder.js";
import { Stats } from "./Stats.js";
import { StatsItem } from "./StatsItem.js";
const cardMinWidth = 224;
function Card({
  children,
  variant = "outlined",
  isSelected = false,
  contextAction,
  onClick,
  onSelect,
  as: asProp = "button",
  href,
  to,
  noMinWidth,
  selectButtonAriaLabel,
  "aria-label": ariaLabel,
  isDisabled = false,
  ...props
}) {
  const cardTitleId = useId("card-title");
  const contextValue = useMemo(() => {
    return {
      variant,
      isSelected,
      cardTitleId
    };
  }, [isSelected, variant, cardTitleId]);
  function handleOnClick(event) {
    if (isDisabled) {
      return;
    }
    if (onSelect && (event.metaKey || event.ctrlKey || event.shiftKey)) {
      onSelect(event);
      event.preventDefault();
      return;
    }
    onClick == null ? void 0 : onClick(event);
  }
  const as = isDisabled ? "div" : asProp;
  return /* @__PURE__ */ jsx(CardContext.Provider, {
    value: contextValue,
    children: /* @__PURE__ */ jsxs(Container, {
      $variant: variant,
      $isSelected: isSelected,
      $noMinWidth: noMinWidth,
      $isDisabled: isDisabled,
      ...props,
      children: [/* @__PURE__ */ jsx(MainAction, {
        as,
        to: isDisabled ? void 0 : to,
        href: as === "a" ? href : void 0,
        type: as === "button" ? "button" : void 0,
        onClick: handleOnClick,
        "aria-labelledby": ariaLabel ? void 0 : cardTitleId,
        "aria-label": ariaLabel,
        $isDisabled: isDisabled,
        children
      }), contextAction && !isDisabled ? /* @__PURE__ */ jsx(ActionContainer, {
        children: contextAction
      }) : null, onSelect ? /* @__PURE__ */ jsx(SelectButton, {
        type: "button",
        $isSelected: isSelected,
        onClick: onSelect,
        "aria-label": selectButtonAriaLabel,
        disabled: isDisabled,
        $isDisabled: isDisabled,
        children: /* @__PURE__ */ jsx(IconCheck, {})
      }) : null]
    })
  });
}
Card.Frame = Frame;
Card.Header = Header;
Card.AdditionalInfo = AdditionalInfo;
Card.ContextActionButton = ContextActionButton;
Card.Images = Images;
Card.Img = Img;
Card.FilePlaceholder = FilePlaceholder;
Card.IconPlaceholder = IconPlaceholder;
Card.Stats = Stats;
Card.StatsItem = StatsItem;
const Container = styled.div.withConfig({
  displayName: "Container",
  componentId: "sc-1tqtj7t-0"
})(["--card-border-radius:", ";--card-inner-border-color:", ";--card-inner-border-width:1px;--card-selected-border-color:", ";--card-selected-border-width:2px;--card-elevation:", ";--card-frame-overlay-color:transparent;--card-actions-opacity:0;&:hover{--card-frame-overlay-color:", ";--card-actions-opacity:1;}&:focus-within{--card-actions-opacity:1;}", " position:relative;box-sizing:border-box;min-width:", ";border-radius:var(--card-border-radius);background:", ";box-shadow:var(--card-box-shadow);pointer-events:", ";"], token.radiusBase, token.gray100a, (props) => props.$isDisabled ? token.gray400 : token.brandPrimary500, (props) => props.$isDisabled ? "0 0 0 transparent" : token.elevation1, token.gray100a, (props) => props.$variant === "outlined" ? css(["--card-frame-border-radius:var(--card-border-radius) var(--card-border-radius) 0 0;--card-box-shadow:var(--card-elevation),inset 0 0 0 var(--card-inner-border-width) var(--card-inner-border-color);--card-frame-border-bottom-width:0;--card-content-padding:", " ", ";&:hover{--card-inner-border-color:", ";--card-elevation:", ";}", ""], token.spacing4, token.spacing5, token.gray200a, token.elevation2, props.$isSelected ? css(["--card-inner-border-width:var(--card-selected-border-width);--card-inner-border-color:var(--card-selected-border-color);&:hover{--card-inner-border-color:var(--card-selected-border-color);}"]) : "") : css(["--card-frame-border-radius:var(--card-border-radius);--card-box-shadow:none;--card-frame-border-bottom-width:1px;--card-content-padding:", " 0;"], token.spacing3), (props) => props.$noMinWidth ? "auto" : `${cardMinWidth}px`, token.gray10, (props) => props.$isDisabled ? "none" : "initial");
const MainAction = styled.button.withConfig({
  displayName: "MainAction",
  componentId: "sc-1tqtj7t-1"
})(["display:block;box-sizing:border-box;width:100%;padding:0;border:none;background:none;font:inherit;text-align:left;text-decoration:none;cursor:", ";"], (props) => props.$isDisabled ? "default" : "pointer");
const SelectButton = styled.button.withConfig({
  displayName: "SelectButton",
  componentId: "sc-1tqtj7t-2"
})(["position:absolute;top:", ";right:", ";z-index:2;display:grid;place-items:center;box-sizing:border-box;width:32px;height:32px;padding:0;border:2px solid rgb(", " / 80%);border-radius:", ";background:none;color:transparent;opacity:", ";pointer-events:", ";&:hover{border-color:", ";}&:active{border-width:3px;border-color:", ";}", ""], token.spacing4, token.spacing4, token["white-rgb"], token.radiusCircle, (props) => props.$isSelected ? "1" : "var(--card-actions-opacity)", (props) => props.$isDisabled ? "none" : "initial", token.white, token.white, (props) => props.$isSelected && css(["border:none;background:", ";color:", ";&:hover{background:", ";box-shadow:", ";}&:active{background:", ";box-shadow:none;}"], props.$isDisabled ? token.gray400 : token.brandPrimary500, token.white, token.brandPrimary600, token.elevation1, token.brandPrimary700));
const ActionContainer = styled.div.withConfig({
  displayName: "ActionContainer",
  componentId: "sc-1tqtj7t-3"
})(["position:absolute;top:", ";left:", ";z-index:2;opacity:var(--card-actions-opacity);"], token.spacing4, token.spacing4);
export {
  Card,
  cardMinWidth
};
//# sourceMappingURL=Card.js.map
